import {
  AppDialog,
  AppInput,
  AppSelectPopover,
  AppTextarea,
  FormLabel,
  type SelectOption,
} from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { TaskMemberSelect } from '@opportunities/-components/TaskMemberSelect'
import type { OpportunityTask } from 'backend/types'
import { DATE, FIELD_MAX_LENGTHS } from 'shared'
import { COLOR_CATEGORY, getColor } from '@/lib/color-registry'
import { useTaskSaveDialog } from './hooks/useTaskSaveDialog'

interface TaskSaveDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  opportunityId: number
  /** 新規作成時に必要。編集時はtaskから取得する */
  salesforceProjectId?: number | null
  task?: OpportunityTask | null
}

export function TaskSaveDialog({ open, onOpenChange, opportunityId, salesforceProjectId, task }: TaskSaveDialogProps) {
  const {
    isEdit,
    effectiveSalesforceProjectId,
    title,
    setTitle,
    description,
    setDescription,
    statusId,
    setStatusId,
    expiredOn,
    setExpiredOn,
    memberId,
    setMemberId,
    statuses,
    errors,
    hasErrors,
    isPending,
    handleChange,
    handleSave,
    handleClose,
  } = useTaskSaveDialog({
    open,
    onOpenChange,
    opportunityId,
    salesforceProjectId,
    task,
  })

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`タスク${isEdit ? '編集' : '追加'}`}
      isPending={isPending}
      className='max-w-150'
      footerOptions={[
        { label: 'キャンセル', onClick: handleClose, variant: 'ghost' },
        {
          label: '保存',
          variant: 'save' as const,
          onClick: handleSave,
          disabled: hasErrors,
          isPending,
          isPendingText: '保存中...',
        },
      ]}
    >
      <div className='space-y-4 py-4'>
        <AppInput
          id='task-title'
          label='タスク名'
          required
          value={title}
          onChange={(e) => {
            setTitle(e.target.value)
            handleChange('title')
          }}
          maxLength={FIELD_MAX_LENGTHS.OPPORTUNITY_TASK_TITLE}
          showCharCount
          placeholder='タスク名を入力'
          disabled={isPending}
          error={errors.title}
        />

        <AppTextarea
          id='task-description'
          label='概要'
          required
          value={description}
          onChange={(e) => {
            setDescription(e.target.value)
            handleChange('description')
          }}
          rows={3}
          maxLength={FIELD_MAX_LENGTHS.OPPORTUNITY_DESCRIPTION}
          placeholder='タスクの概要を入力'
          disabled={isPending}
          error={errors.description}
          showCharCount
          limitHeight='sm'
        />

        <div className='grid grid-cols-2 gap-4'>
          {/* ステータス */}
          <div className='space-y-1'>
            {/* TODO: エラーが独自実装 */}
            <FormLabel htmlFor='task-status' required>
              ステータス
            </FormLabel>
            <AppSelectPopover
              currentValue={statusId?.toString() ?? ''}
              options={statuses.map(
                (s): SelectOption => ({
                  value: String(s.id),
                  label: s.name,
                  color: getColor(COLOR_CATEGORY.TASK_STATUS, s.id),
                }),
              )}
              onSelect={(value) => {
                setStatusId(Number(value))
                handleChange('statusId')
              }}
              disabled={isPending}
              triggerVariant='button'
              placeholder='ステータスを選択'
              align='start'
            />
            {errors.statusId && <p className='text-sm text-error'>{errors.statusId}</p>}
          </div>

          {/* 期限(入力日~入力日の1年後の日付が入力可能) */}
          <AppInput
            id='task-expired-on'
            label='期限'
            type='date'
            value={expiredOn}
            onChange={(e) => setExpiredOn(e.target.value)}
            disabled={isPending}
            min={DATE.today}
            max={DATE.oneYearLater}
          />
        </div>

        {/* 担当者 */}
        <TaskMemberSelect
          id='task-member'
          value={memberId}
          onChange={(value) => {
            setMemberId(value)
            handleChange('memberId')
          }}
          salesforceProjectId={effectiveSalesforceProjectId}
          required
          disabled={isPending}
          error={errors.memberId}
        />
      </div>
    </AppDialog>
  )
}
